/**
 * Who can do what in the console, and the advice that follows (rd r12 A5-5;
 * rc-5 §1b, §3C).
 *
 * WHY THIS EXISTS. Role advice was hand-written about 160 times, so it drifted:
 * the account manager was told to ask the owner for things the account manager
 * can do (press Check now, read the books), and both roles were told to ask for
 * things nobody can do in the console. This module is the one place an action's
 * audience is decided.
 *
 * WHAT A ROW SAYS. `owner` / `accountManager` say whether that role can do the
 * action at all. `control` is the console control that does it — the words a
 * person reads on the button, or the page's nav label — or `"ledger_by_hand"`
 * when no console control exists (decision 2: an action nobody can do in the
 * console is done in the ledger by hand for now). `hook` is the machine
 * identity of that control: the button's `data-action` value, or the page's
 * route. The lane's guard checks it against the program, so a row cannot send a
 * person to a control that does not exist.
 *
 * WHY TWO FIELDS FOR ONE CONTROL. A `data-action` value (`check-now`) is an
 * internal key and must never reach a face; the words (`Check now`) must. One
 * field cannot hold both, and a second hand-kept table would drift. So the row
 * carries the words and the key, and a test binds them to the program.
 *
 * `worker` names the Worker functions that enforce the rule, as
 * `"<file>#<function>"`. `admin-portal/test/rd-r12-a5b-capability-drift.test.ts`
 * reads `ingest-worker/src` and checks every `requireOwner(` call site is
 * covered, so a new owner-only handler cannot appear without a row here.
 */

import { isShellRoute } from "../nav-registry.js";
import type { StaffRole } from "../types.js";
import { navLabel, sentence } from "./sentences.js";

export type CapabilityAction =
  | "check_now"
  | "open_books"
  | "view_intercompany_check"
  | "reopen_card"
  | "reconnect_bank"
  | "issue_bank_link"
  | "revoke_bank_link"
  | "confirm_statement"
  | "change_rule"
  | "approve_rule"
  | "reject_change_request"
  | "return_card_to_am"
  | "issue_access_key"
  | "revoke_access_key"
  | "view_access_keys"
  | "register_shared_card"
  | "view_shared_cards"
  | "manage_groups"
  | "view_expense_audit"
  | "review_expense_audit"
  | "decide_expense_audit"
  | "cancel_expense_audit"
  | "attach_file"
  | "withdraw_attachment"
  | "approve_onboarding_ticket"
  | "reveal_onboarding_ticket"
  | "view_agent_identities"
  | "request_report_window"
  | "view_tax_totals"
  | "confirm_onboarding_step"
  | "confirm_opening_determination"
  | "open_account"
  | "add_place"
  | "map_bank"
  | "reopen_month"
  | "switch_on_books_view"
  | "switch_on_hub_intake";

export interface Capability {
  readonly action: CapabilityAction;
  readonly owner: boolean;
  readonly accountManager: boolean;
  /**
   * The words on the control, or a page's nav label; `"ledger_by_hand"` when no
   * console control exists (decision 2).
   */
  readonly control: string;
  /** The button's `data-action` value, the page's route, or `""` by hand. */
  readonly hook: string;
  /** The account manager's next step when only the owner can do it. */
  readonly askOwner: string;
  /**
   * True when the account manager may do the action, but not on a card the
   * owner currently holds: the Worker refuses an AM reopening an escalated
   * card (`ingest-worker/src/admin.ts:2191`). The owner is never blocked.
   */
  readonly exceptEscalatedCard?: boolean;
  /**
   * True when the account manager may do the action, but not on a booking
   * whose month is closed: owner decision 5 (2026-11-02, round 15) — "The AM
   * can change categories and see receipt images; every change records who;
   * changing a CLOSED month stays the owner's." The Worker refuses an AM
   * decision or cancel on a locked period (`ingest-worker/src/expense_audit.ts`).
   */
  readonly exceptClosedMonth?: boolean;
  /**
   * True when the account manager may do the action, but only on the file they
   * uploaded: owner amendment 2026-11-02 — "An AM could accidentally upload a
   * photo so they should be able to remove one they add - the owner can remove
   * their own upload or an am upload." The Worker refuses an AM withdrawing a
   * file someone else uploaded (`ingest-worker/src/transaction_attachments.ts`).
   */
  readonly exceptNotOwnUpload?: boolean;
  /** The Worker functions that enforce it, as `"<file>#<function>"`. */
  readonly worker: readonly string[];
}

/** What the reader is looking at when advice is rendered. */
export interface AdviceContext {
  /** The card in front of the reader is currently escalated. */
  readonly escalatedCard?: boolean;
  /**
   * The booking in front of the reader sits in a closed month. An account
   * manager may change a category, but not one in a closed month (owner
   * decision 5, 2026-11-02).
   */
  readonly closedMonth?: boolean;
  /**
   * The file in front of the reader was uploaded by the reader. An account
   * manager may withdraw only their own upload (owner amendment 2026-11-02).
   * Unspecified fails closed: a caller that does not know whose file it is
   * does not get the account manager the control.
   */
  readonly ownUpload?: boolean;
}

/** Decision 2's control: no console control exists for this action yet. */
const BY_HAND = "ledger_by_hand" as const;

/**
 * Round 2 review, M1: the switch is not ledger content, so it does not read
 * "added in the ledger by hand". Its `control` points at this key, and
 * `advice` renders the matching sentence (orchestrator wording).
 */
const SWITCHED_OUTSIDE = "switched_outside_console" as const;

export const CAPABILITIES: readonly Capability[] = [
  // --- Both roles ---------------------------------------------------------
  {
    action: "check_now",
    owner: true,
    accountManager: true,
    control: "Check now",
    hook: "check-now",
    askOwner: "check now",
    // roleMayCheckNow: an explicit allow-list, so a later staff role does not
    // inherit the press.
    worker: ["check_now.ts#roleMayCheckNow"],
  },
  {
    action: "open_books",
    owner: true,
    accountManager: true,
    control: navLabel("general-ledger"),
    hook: "general-ledger",
    askOwner: `open ${navLabel("general-ledger")}`,
    // roleMayReadBooks: an explicit allow-list for the books screens.
    worker: ["console.ts#roleMayReadBooks"],
  },
  {
    action: "view_intercompany_check",
    owner: true,
    accountManager: true,
    control: navLabel("intercompany"),
    hook: "intercompany",
    askOwner: `open ${navLabel("intercompany")}`,
    // The intercompany read is a books read (roleMayReadBooks), not requireOwner.
    worker: ["intercompany.ts#getAdminIntercompany", "console.ts#roleMayReadBooks"],
  },
  {
    action: "reopen_card",
    owner: true,
    accountManager: true,
    control: "Reopen",
    hook: "reopen-card",
    askOwner: "reopen the card",
    // roleMayReopenCards (G6, owner 2026-10-25). The allow-list says both
    // roles, and the Worker then refuses an AM on a CURRENTLY ESCALATED card
    // (admin.ts:2191). The ordinary card is the AM's; the escalated one is the
    // owner's, so the advice must not offer the AM a control that would 403.
    exceptEscalatedCard: true,
    worker: ["admin.ts#roleMayReopenCards", "admin.ts#reopenQueueItem"],
  },

  // --- Owner only, with a console control ---------------------------------
  {
    action: "reconnect_bank",
    owner: true,
    accountManager: false,
    control: "Reconnect",
    hook: "reconnect-bank",
    // The decided sentence (rd-z §3): the AM asks the owner to reconnect.
    askOwner: "reconnect the bank",
    // A bank-link invitation initiates linking outside Access (KPB1).
    worker: ["admin.ts#issueInvitation"],
  },
  {
    action: "issue_bank_link",
    owner: true,
    accountManager: false,
    control: "Issue Link invitation",
    hook: "issue-link-invitation",
    askOwner: "issue a bank link",
    worker: ["admin.ts#issueInvitation"],
  },
  {
    action: "revoke_bank_link",
    owner: true,
    accountManager: false,
    control: "Revoke",
    hook: "revoke-invite",
    askOwner: "revoke the bank link",
    worker: ["admin.ts#revokeInvitation"],
  },
  {
    action: "confirm_statement",
    owner: true,
    accountManager: false,
    control: "Confirm",
    hook: "confirm-statement",
    askOwner: "confirm the statement",
    // KPB4-authz: a confirmation writes ledger-affecting fingerprints. The
    // revoke endpoint restores a prior active fingerprint when a confirm fails,
    // so it is part of the same capability (the console has no separate control
    // for it).
    worker: [
      "admin.ts#confirmStatementReconciliation",
      "admin.ts#revokeStatementFingerprint",
    ],
  },
  {
    action: "change_rule",
    owner: true,
    accountManager: false,
    control: navLabel("rules"),
    hook: "rules",
    askOwner: "change the rule",
    worker: ["admin.ts#requestRuleChange"],
  },
  {
    action: "approve_rule",
    owner: true,
    accountManager: false,
    control: "Approve standing rule",
    hook: "approve-rule-request",
    askOwner: "approve the rule request",
    worker: ["admin.ts#decideLedgerChangeRequest"],
  },
  {
    // The Rules page lists account, bank mapping and place requests that are no
    // longer offered: nothing applies them, so the only way off the list is the
    // owner's Reject. The account manager reads the owner's step from here
    // rather than from a hand-typed sentence (rd r13 review 1, item 4).
    action: "reject_change_request",
    owner: true,
    accountManager: false,
    control: "Reject",
    hook: "reject-ledger-change",
    askOwner: "reject the request",
    worker: ["admin.ts#decideLedgerChangeRequest"],
  },
  {
    action: "return_card_to_am",
    owner: true,
    accountManager: false,
    control: "Return to account manager",
    hook: "return-to-am",
    askOwner: "return the card",
    worker: ["admin.ts#returnQueueItemToAm"],
  },
  {
    action: "issue_access_key",
    owner: true,
    accountManager: false,
    control: "Issue API key",
    hook: "issue-api-key",
    askOwner: "create an access key",
    worker: ["admin.ts#issueToken"],
  },
  {
    action: "revoke_access_key",
    owner: true,
    accountManager: false,
    control: "Revoke",
    hook: "revoke-token",
    askOwner: "revoke the access key",
    worker: ["admin.ts#revokeToken"],
  },
  {
    action: "view_access_keys",
    owner: true,
    accountManager: false,
    control: navLabel("tokens"),
    hook: "tokens",
    askOwner: `open ${navLabel("tokens")}`,
    worker: ["admin.ts#listTokens"],
  },
  {
    action: "register_shared_card",
    owner: true,
    accountManager: false,
    control: "Save",
    hook: "card-save",
    askOwner: "add the shared card",
    worker: ["admin.ts#putOwnerIcSplitCard"],
  },
  {
    action: "view_shared_cards",
    owner: true,
    accountManager: false,
    control: navLabel("cards"),
    hook: "cards",
    askOwner: `open ${navLabel("cards")}`,
    worker: ["admin.ts#getOwnerIcSplitCards"],
  },
  {
    action: "manage_groups",
    owner: true,
    accountManager: false,
    control: navLabel("groups"),
    hook: "groups",
    askOwner: "change the groups",
    worker: [
      "admin.ts#listOwnerDashboardGroups",
      "admin.ts#createOwnerDashboardGroup",
      "admin.ts#patchOwnerDashboardGroup",
      "admin.ts#setOwnerDefaultDashboardGroup",
    ],
  },
  {
    // Renamed from `open_expense_audit_document` (recount, M1): the row also
    // names the page's views list and the minted document URLs, so it is the
    // view surface of the Expense audit page, not one endpoint.
    action: "view_expense_audit",
    owner: true,
    accountManager: false,
    control: navLabel("expense-audit"),
    hook: "expense-audit",
    askOwner: `open ${navLabel("expense-audit")}`,
    // The page and its saved views are the owner's. Minting a document URL is
    // no longer owner-only (owner decision 5, 2026-11-02: the account manager
    // may see receipt images), so it is not this row's gate; the page's views
    // list is.
    worker: [
      "hub_documents.ts#mintHubDocumentUrls",
      "expense_audit.ts#adminExpenseAuditViews",
    ],
  },
  {
    // Recount, M1: the review, decision and cancel writes sit behind
    // `expense_audit.ts#requireAdminCompany` (requireQueueCompany in owner
    // mode), which the drift test previously skipped as a read dispatcher.
    action: "review_expense_audit",
    owner: true,
    accountManager: false,
    control: navLabel("expense-audit"),
    hook: "expense-audit",
    askOwner: `review a receipt in ${navLabel("expense-audit")}`,
    worker: ["expense_audit.ts#adminExpenseAuditReview"],
  },
  {
    action: "decide_expense_audit",
    owner: true,
    // Owner decision 5 (2026-11-02, round 15): "The AM can change categories
    // and see receipt images; every change records who; changing a CLOSED
    // month stays the owner's." Every change is a new ledger entry whose
    // source carries the person's slug (reclass-{user}), so the console's
    // one Change category control is the account manager's too.
    accountManager: true,
    control: "Change category",
    hook: "change-category",
    askOwner: "change the category",
    exceptClosedMonth: true,
    worker: [
      "expense_audit.ts#adminExpenseAuditDecision",
      "expense_audit.ts#requireQueueCompany",
      "console.ts#roleMayReadBooks",
    ],
  },
  {
    action: "cancel_expense_audit",
    owner: true,
    // Owner decision 7 (2026-11-02): "Undo" is changing the category again, or
    // cancelling a pending change before the nightly run applies it. The same
    // role rule applies, and a closed month stays the owner's (decision 5).
    accountManager: true,
    control: "Cancel the change",
    hook: "cancel-category-change",
    askOwner: "cancel the pending change",
    exceptClosedMonth: true,
    worker: [
      "expense_audit.ts#adminExpenseAuditCancel",
      "expense_audit.ts#requireQueueCompany",
      "console.ts#roleMayReadBooks",
    ],
  },
  {
    // Owner decision 8 (2026-11-02, round 15): "if it is simple to attach
    // something to a transaction, we should have it ... Just a simple upload of
    // an image or file that attaches to the transaction." Both roles attach:
    // the file is a copy of a document the line already rests on, it is never
    // read, and it never changes a booking, so there is nothing here for the
    // owner alone to authorise. The Worker's console read gate is the enforcement
    // (`requireConsoleCompany`: admin:console, owner or account manager).
    action: "attach_file",
    owner: true,
    accountManager: true,
    control: "Attach file",
    hook: "attach-file",
    askOwner: "attach the file",
    worker: [
      "transaction_attachments.ts#uploadTransactionAttachment",
      "transaction_attachments.ts#listTransactionAttachments",
      "transaction_attachments.ts#mintTransactionAttachmentUrl",
      "console.ts#roleMayReadBooks",
    ],
  },
  {
    // Owner amendment 2026-11-02, verbatim: "An AM could accidentally upload a
    // photo so they should be able to remove one they add - the owner can remove
    // their own upload or an am upload." Both roles may withdraw, but an account
    // manager only a file they uploaded: `exceptNotOwnUpload` fails closed when
    // the caller does not say whose file it is, and the Worker enforces the same
    // rule (`withdrawTransactionAttachment` returns 403 for another's upload).
    action: "withdraw_attachment",
    owner: true,
    accountManager: true,
    control: "Withdraw",
    hook: "withdraw-attachment",
    askOwner: "withdraw the file",
    exceptNotOwnUpload: true,
    worker: [
      "transaction_attachments.ts#withdrawTransactionAttachment",
      "console.ts#roleMayReadBooks",
    ],
  },
  {
    action: "approve_onboarding_ticket",
    owner: true,
    accountManager: false,
    control: "Approve this plan",
    hook: "approve-approval",
    askOwner: "approve the onboarding plan",
    // requireHumanOwner: the approval write is a human-owner session only.
    worker: ["onboarding_approvals.ts#approveOnboardingTicket"],
  },
  {
    action: "reveal_onboarding_ticket",
    owner: true,
    accountManager: false,
    control: "Reveal now",
    hook: "reveal-approval",
    askOwner: "reveal the onboarding ticket",
    // The secret is minted at reveal time and returned once; human owner only.
    worker: ["onboarding_approvals.ts#revealOnboardingTicket"],
  },
  {
    action: "view_agent_identities",
    owner: true,
    accountManager: false,
    control: navLabel("agent-access"),
    hook: "agent-access",
    askOwner: `open ${navLabel("agent-access")}`,
    // The agent-identity list and create routes are a human owner's only.
    worker: ["onboarding_agent_routes.ts#handleAgentIdentities"],
  },
  {
    // rd15 A9: the Tax time page (the 1099 totals download). Both roles may
    // read a saved window, so the row says what the page is and who may open
    // it; nothing on it writes.
    action: "view_tax_totals",
    owner: true,
    accountManager: true,
    control: navLabel("tax-time"),
    hook: "tax-time",
    askOwner: `open ${navLabel("tax-time")}`,
    worker: ["report_windows.ts#listAdminReportWindows", "report_windows.ts#getAdminReportWindow"],
  },
  {
    action: "request_report_window",
    owner: true,
    accountManager: false,
    control: navLabel("reports"),
    hook: "reports",
    askOwner: "request the report window",
    // The console's owner company gate guards the request (requireConsoleCompanyOwner).
    worker: ["report_windows.ts#requestAdminReportWindow"],
  },
  {
    action: "confirm_onboarding_step",
    owner: true,
    accountManager: false,
    control: "Mark portal done",
    hook: "confirm-step",
    askOwner: "confirm the step",
    worker: ["onboarding_runs.ts#confirmOnboardingStep"],
  },
  {
    action: "confirm_opening_determination",
    owner: true,
    accountManager: false,
    control: "Confirm extract",
    hook: "determination-confirm",
    askOwner: "confirm the opening figures",
    worker: [
      "opening_determination_events.ts#putOpeningDetermination",
      "opening_determination_events.ts#companiesAffectedByOpeningDocument",
    ],
  },

  // --- Decision 2: nobody can do it in the console ------------------------
  {
    action: "open_account",
    owner: false,
    accountManager: false,
    control: BY_HAND,
    hook: "",
    askOwner: "",
    worker: [],
  },
  {
    action: "add_place",
    owner: false,
    accountManager: false,
    control: BY_HAND,
    hook: "",
    askOwner: "",
    worker: [],
  },
  {
    action: "map_bank",
    owner: false,
    accountManager: false,
    control: BY_HAND,
    hook: "",
    askOwner: "",
    worker: [],
  },
  {
    action: "reopen_month",
    owner: false,
    accountManager: false,
    control: BY_HAND,
    hook: "",
    askOwner: "",
    worker: [],
  },
  // Switching the Books view or the hub intake on is a company setting made
  // outside the console (GitHub variables and D1 settings), not a console
  // control and not ledger content. Round 2 review, M1: the sentence is
  // `switched_outside_console`, never decision 2's ledger wording.
  {
    action: "switch_on_books_view",
    owner: false,
    accountManager: false,
    control: SWITCHED_OUTSIDE,
    hook: "",
    askOwner: "",
    worker: [],
  },
  {
    action: "switch_on_hub_intake",
    owner: false,
    accountManager: false,
    control: SWITCHED_OUTSIDE,
    hook: "",
    askOwner: "",
    worker: [],
  },
];

export const CAPABILITY_ACTIONS: readonly CapabilityAction[] = CAPABILITIES.map(
  (capability) => capability.action,
);

const BY_ACTION: ReadonlyMap<CapabilityAction, Capability> = new Map(
  CAPABILITIES.map((capability) => [capability.action, capability]),
);

/** The row for an action. An unknown action throws: a typo must not read "no". */
export function capabilityOf(action: CapabilityAction): Capability {
  const capability = BY_ACTION.get(action);
  if (capability === undefined) {
    throw new Error(`capabilities: unknown action "${action}"`);
  }
  return capability;
}

/**
 * Whether `role` can do `action`, in the console or in the ledger by hand.
 *
 * This answers the ROLE question only. A conditional gate (an escalated card,
 * a closed month) is `mayWithContext`, because a caller that has not said which
 * card it is rendering must not be told "yes".
 */
export function can(role: StaffRole, action: CapabilityAction): boolean {
  const capability = capabilityOf(action);
  return role === "owner" ? capability.owner : capability.accountManager;
}

/**
 * Whether `role` can do `action` for the thing in front of them, conditional
 * gates included. `mayWithContext` and `advice` share `blockedByContext`, so a
 * control and its sentence cannot disagree about the escalated card or the
 * closed month.
 */
export function mayWithContext(
  action: CapabilityAction,
  role: StaffRole,
  context: AdviceContext = {},
): boolean {
  const capability = capabilityOf(action);
  return canRow(capability, role) && !blockedByContext(capability, role, context);
}

/**
 * True when no role can do the action in the console: the decision-2 set. Both
 * roles read the same sentence, because the answer is the same for both.
 */
export function nobodyCanInConsole(action: CapabilityAction): boolean {
  const capability = capabilityOf(action);
  return !capability.owner && !capability.accountManager;
}

/**
 * The next step for `role`, in one sentence:
 *  - the role can do it → names the console control ("Press Check now.",
 *    "Open Books."), or decision 2's sentence when the control is the ledger;
 *  - only the owner can → "Ask the owner to …" for the account manager;
 *  - nobody can → decision 2's sentence, for both roles.
 *
 * `context` is what the reader is looking at. On an escalated card the account
 * manager cannot reopen it (admin.ts:2191), so the AM reads the ask-owner
 * sentence instead of a control the Worker would refuse.
 */
export function advice(
  action: CapabilityAction,
  role: StaffRole,
  context: AdviceContext = {},
): string {
  return adviceForCapability(capabilityOf(action), role, context);
}

/**
 * `advice` for one row. Exported so a guard can render a virtual row through the
 * same code the real table uses (the planted-defect test), instead of a second
 * copy of these rules.
 */
export function adviceForCapability(
  capability: Capability,
  role: StaffRole,
  context: AdviceContext = {},
): string {
  // A switch turned on outside the console has its own sentence (round 2
  // review, M1): it is not ledger content, so it must not read decision 2's
  // "added in the ledger by hand". The `control` field names the row.
  if (capability.control === SWITCHED_OUTSIDE) {
    return sentence("switched_outside_console", role);
  }
  if (nobodyCanInConsoleRow(capability)) {
    return sentence("ledger_by_hand", role);
  }
  if (!canRow(capability, role) || blockedByContext(capability, role, context)) {
    return `Ask the owner to ${capability.askOwner}.`;
  }
  if (capability.control === BY_HAND) {
    return sentence("ledger_by_hand", role);
  }
  return isShellRoute(capability.hook)
    ? `Open ${capability.control}.`
    : `Press ${capability.control}.`;
}

/**
 * A conditional gate: the AM may do this only when the context positively says
 * the card is not escalated. An unspecified context fails closed (recount,
 * LOW 2): a caller that does not know which card it is rendering must not
 * offer a control the Worker would refuse.
 */
function blockedByContext(
  capability: Capability,
  role: StaffRole,
  context: AdviceContext,
): boolean {
  if (role === "owner") return false;
  if (
    capability.exceptEscalatedCard === true &&
    // An unspecified context fails closed: a caller that does not know which
    // card it is rendering must not offer a control the Worker would refuse.
    context.escalatedCard !== false
  ) {
    return true;
  }
  return (
    capability.exceptClosedMonth === true &&
    // Same fail-closed rule: a caller that has not said whether the month is
    // closed does not get the account manager the control.
    context.closedMonth !== false
  ) || (
    capability.exceptNotOwnUpload === true &&
    // A caller that has not said whose file it is does not get the AM the
    // control: the Worker would refuse another person's upload.
    context.ownUpload !== true
  );
}

function canRow(capability: Capability, role: StaffRole): boolean {
  return role === "owner" ? capability.owner : capability.accountManager;
}

function nobodyCanInConsoleRow(capability: Capability): boolean {
  return !capability.owner && !capability.accountManager;
}
